import type { ChatVectorIndexOption } from "src/db";

export type DatasourceKind = "asset" | "document";

const normalize = (value: string | null | undefined): string =>
    (value ?? "").toLowerCase().replace(/[_\s]/g, "");

// Whether a datasource is an asset datasource is driven by the `asset_datasource`
// flag returned per datasource by /v1/system/configuration (see flattenDatasources),
// not a hardcoded id list.
const isAsset = (option: ChatVectorIndexOption): boolean => option.asset_datasource === true;

export const matchesUserRole = ({
    option,
    userRoles,
}: {
    option: ChatVectorIndexOption;
    userRoles: string[];
}): boolean =>
    userRoles.some((role) => {
        const normalizedRole = normalize(role);
        const datasourceLabel = normalize(option.datasource_label);
        const indexLabel = normalize(option.label);
        return (
            normalizedRole.includes(datasourceLabel) ||
            datasourceLabel.includes(normalizedRole) ||
            normalizedRole.includes(indexLabel) ||
            indexLabel.includes(normalizedRole) ||
            normalizedRole === normalize(option.datasource_id)
        );
    });

// Whether a datasource matches the browser it is being listed for. The asset/document
// split only exists when the ASSETS_VIEWER feature is on; with it off every datasource
// belongs to the documents viewer and the assets viewer lists nothing.
const matchesRequestedKind = ({
    option,
    kind,
    assetsViewerEnabled,
}: {
    option: ChatVectorIndexOption;
    kind: DatasourceKind;
    assetsViewerEnabled: boolean;
}): boolean => {
    if (!assetsViewerEnabled) return kind === "document";
    return kind === "asset" ? isAsset(option) : isAsset(option) === false;
};

// Resolves a tab id (either `datasource_id` or `datasource_id/index_id`) to its kind
// using the loaded datasource options. Returns `undefined` when the datasource is not
// (yet) known, so callers don't misclassify a tab before the configuration loads.
// With the ASSETS_VIEWER feature off there is no split, so every tab is a document.
export const datasourceKindOfTab = (
    tab: string | undefined,
    options: ChatVectorIndexOption[],
    assetsViewerEnabled: boolean,
): DatasourceKind | undefined => {
    if (!tab) return undefined;
    if (!assetsViewerEnabled) return "document";
    const datasourceId = tab.split("/")[0];
    const match = options.find((option) => option.datasource_id === datasourceId);
    if (!match) return undefined;
    return isAsset(match) ? "asset" : "document";
};

export const visibleDatasources = ({
    all,
    userRoles,
    kind,
    requireIndex = false,
    availableAlwaysOnIds = new Set(),
    assetsViewerEnabled,
}: {
    all: ChatVectorIndexOption[];
    userRoles: string[];
    kind: DatasourceKind;
    requireIndex?: boolean;
    availableAlwaysOnIds?: Set<string>;
    assetsViewerEnabled: boolean;
}): ChatVectorIndexOption[] =>
    all.filter((option) => {
        const hasIndex = requireIndex === false || option.index_id !== "";
        const matchesKind = matchesRequestedKind({ option, kind, assetsViewerEnabled });
        const hasAccess =
            availableAlwaysOnIds.has(option.datasource_id) ||
            matchesUserRole({ option, userRoles });
        return hasIndex && matchesKind && hasAccess;
    });
